import { motion } from 'framer-motion';
import { Clock, MapPin, Trophy } from 'lucide-react';

const shakeVariants = {
  shake: { x: [0, -8, 8, -6, 6, -3, 3, 0], transition: { duration: 0.5 } },
};
const popVariants = {
  pop: { scale: [1, 1.06, 1], transition: { duration: 0.35, ease: 'easeOut' } },
};

interface SituationalPlayCallProps {
  question: string;
  situationContext: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  onAnswer: (index: number) => void;
  selectedAnswer: number | null;
  isCorrect: boolean | null;
}

export function SituationalPlayCall({
  question,
  situationContext,
  options,
  correctIndex,
  onAnswer,
  selectedAnswer,
  isCorrect,
}: SituationalPlayCallProps) {
  const answered = selectedAnswer !== null;

  // Parse context lines for visual display
  const contextLines = situationContext.split('|').map(s => s.trim());

  return (
    <div className="w-full space-y-5">
      <div className="text-center">
        <span className="inline-block bg-amber-500/15 text-amber-600 dark:text-amber-400 font-display font-bold text-xs px-3 py-1 rounded-full mb-3">
          🎯 PLAY CALL
        </span>
      </div>

      {/* Situation context card */}
      <div className="bg-secondary rounded-2xl border-2 border-amber-500/20 p-4 space-y-2">
        <p className="font-display font-bold text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Game Situation
        </p>
        <div className="flex flex-wrap gap-2">
          {contextLines.map((line, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 text-sm font-body font-medium bg-background/80 text-foreground px-3 py-1.5 rounded-lg"
            >
              {i === 0 && <Clock className="w-3.5 h-3.5 text-muted-foreground" />}
              {i === 1 && <MapPin className="w-3.5 h-3.5 text-muted-foreground" />}
              {i === 2 && <Trophy className="w-3.5 h-3.5 text-muted-foreground" />}
              {line}
            </span>
          ))}
        </div>
      </div>

      {/* Question */}
      <p className="font-body font-semibold text-base text-center leading-snug">
        {question}
      </p>

      {/* Options */}
      <div className="space-y-2.5">
        {options.map((option, i) => {
          const isSelected = i === selectedAnswer;
          const isCorrectOption = i === correctIndex;

          let variant = 'bg-card border-border hover:border-amber-500/50';
          if (answered) {
            if (isCorrectOption) {
              variant = 'bg-success/10 border-success ring-2 ring-success/30';
            } else if (isSelected && !isCorrect) {
              variant = 'bg-destructive/10 border-destructive ring-2 ring-destructive/30';
            } else {
              variant = 'bg-muted/50 border-border opacity-50';
            }
          }

          const animateState = answered
            ? isSelected && isCorrect ? 'pop'
            : isSelected && !isCorrect ? 'shake'
            : undefined
            : undefined;

          return (
            <motion.button
              key={i}
              onClick={() => !answered && onAnswer(i)}
              disabled={answered}
              whileTap={!answered ? { scale: 0.98 } : {}}
              variants={{ ...shakeVariants, ...popVariants }}
              animate={animateState}
              className={`w-full p-4 rounded-xl border-2 text-left font-body font-medium transition-colors ${variant}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-sm text-muted-foreground">
                  {String.fromCharCode(65 + i)}.
                </span>
                <span className="flex-1">{option}</span>
                {answered && isCorrectOption && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }} className="text-success text-lg">✅</motion.span>
                )}
                {answered && isSelected && !isCorrect && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }} className="text-destructive text-lg">❌</motion.span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
